import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import {
  Plus,
  BellRing,
  Edit2,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Briefcase,
  MapPin,
  GraduationCap,
  Search,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AlertsPage = () => {
  const { token } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);

  const fetchAlerts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/alerts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAlerts(response.data);
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
      toast.error('Failed to load alerts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAlerts();
  }, [token]);

  const toggleAlert = async (alertId, currentActive) => {
    try {
      const alert = alerts.find((a) => a.id === alertId);
      await axios.put(
        `${API_URL}/api/alerts/${alertId}`,
        { ...alert, active: !currentActive },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAlerts(alerts.map((a) => (a.id === alertId ? { ...a, active: !currentActive } : a)));
      toast.success(`Alert ${!currentActive ? 'activated' : 'paused'}`);
    } catch (error) {
      toast.error('Failed to update alert');
    }
  };

  const deleteAlert = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API_URL}/api/alerts/${deleteId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAlerts(alerts.filter((a) => a.id !== deleteId));
      toast.success('Alert deleted');
    } catch (error) {
      toast.error('Failed to delete alert');
    } finally {
      setDeleteId(null);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="alerts-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Job Alerts</h1>
            <p className="text-slate-600 mt-1">Manage your healthcare job alert preferences</p>
          </div>
          <Link to="/alerts/new">
            <Button className="bg-[#00A651] hover:bg-[#008C44] text-white rounded-full" data-testid="new-alert-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Alert
            </Button>
          </Link>
        </div>

        {/* Alerts List */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-xl border border-slate-200 p-6 animate-pulse">
                <div className="h-6 w-48 bg-slate-200 rounded mb-4" />
                <div className="h-4 w-full bg-slate-100 rounded" />
              </div>
            ))}
          </div>
        ) : alerts.length > 0 ? (
          <div className="space-y-4">
            {alerts.map((alert) => (
              <div
                key={alert.id}
                className={`bg-white rounded-xl border p-6 card-hover ${
                  alert.active ? 'border-slate-200' : 'border-slate-100 opacity-60'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        alert.active ? 'bg-[#E2F5EA]' : 'bg-slate-100'
                      }`}>
                        <BellRing className={`w-5 h-5 ${alert.active ? 'text-[#00A651]' : 'text-slate-400'}`} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg text-slate-900">{alert.name}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          alert.active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-slate-100 text-slate-600'
                        }`}>
                          {alert.active ? 'Active' : 'Paused'}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-3 text-sm text-slate-600">
                      {alert.occupation && (
                        <span className="flex items-center gap-1 bg-slate-50 px-2 py-1 rounded">
                          <Briefcase className="w-3.5 h-3.5" />
                          {alert.occupation}
                        </span>
                      )}
                      {alert.grade && (
                        <span className="flex items-center gap-1 bg-slate-50 px-2 py-1 rounded">
                          <GraduationCap className="w-3.5 h-3.5" />
                          {alert.grade}
                        </span>
                      )}
                      {alert.location && (
                        <span className="flex items-center gap-1 bg-slate-50 px-2 py-1 rounded">
                          <MapPin className="w-3.5 h-3.5" />
                          {alert.location}
                        </span>
                      )}
                      {alert.keywords && (
                        <span className="flex items-center gap-1 bg-slate-50 px-2 py-1 rounded">
                          <Search className="w-3.5 h-3.5" />
                          "{alert.keywords}"
                        </span>
                      )}
                    </div>

                    <p className="text-sm text-slate-500 mt-3">
                      {alert.matches_count} job{alert.matches_count !== 1 ? 's' : ''} matched
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => toggleAlert(alert.id, alert.active)}
                      className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
                      title={alert.active ? 'Pause alert' : 'Activate alert'}
                      data-testid={`toggle-alert-${alert.id}`}
                    >
                      {alert.active ? (
                        <ToggleRight className="w-6 h-6 text-[#00A651]" />
                      ) : (
                        <ToggleLeft className="w-6 h-6 text-slate-400" />
                      )}
                    </button>
                    <Link to={`/alerts/${alert.id}/edit`}>
                      <button
                        className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
                        title="Edit alert"
                        data-testid={`edit-alert-${alert.id}`}
                      >
                        <Edit2 className="w-5 h-5 text-slate-600" />
                      </button>
                    </Link>
                    <button
                      onClick={() => setDeleteId(alert.id)}
                      className="p-2 rounded-lg hover:bg-red-50 transition-colors"
                      title="Delete alert"
                      data-testid={`delete-alert-${alert.id}`}
                    >
                      <Trash2 className="w-5 h-5 text-red-500" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <div className="w-20 h-20 rounded-full bg-[#E2F5EA] flex items-center justify-center mx-auto mb-6">
              <BellRing className="w-10 h-10 text-[#00A651]" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2">No alerts yet</h3>
            <p className="text-slate-600 mb-6 max-w-md mx-auto">
              Create your first job alert to start receiving notifications for healthcare positions matching your criteria.
            </p>
            <Link to="/alerts/new">
              <Button className="bg-[#00A651] hover:bg-[#008C44] text-white rounded-full px-8" data-testid="create-first-alert-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Alert
              </Button>
            </Link>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Alert</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this alert? This action cannot be undone and all associated job matches will be removed.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={deleteAlert}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};

export default AlertsPage;
